/**
 * @file Detail page for a single back order (delivery).
 */



import React from "react";
import { useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import DetailsCard from "../general/DetailsCard";
import PageTitle from "../general/PageTitle";
import PagedTable from "../table/PagedTable";
import Loading from "../general/Loading";
import usePagination from "../../helpers/hooks/usePagination";
import NavigationLink from "../general/links/NavigationLink";
import DashboardLink from "../general/links/DashboardLink";
import CurrentPage from "../general/links/CurrentPage";
import {backOrderInformation, backOrderItemsColumn} from "../../helpers/constants/backOrderDetailsConstants";
import BackOrderDetailsList from "./BackOrderDetailsList";
import useQueryUrl from "../../helpers/hooks/useQueryUrl";



// Renders detail page for individual orders
/**
 * Renders the detail page for one back order.
 *
 * Reads the delivery ID from the route (`:id`) and fetches the delivery from `/backorders/<id>` (ViewBackOrders.get_object on the backend).
 * That response includes only the lines that still have quantity left to ship.
 * Shows a loading indicator until the data arrives.
 *
 * Layout:
 * - Page title and breadcrumb links: Dashboard > Back Orders > Back Order Details.
 * - Left (4 of 12 columns on medium screens and up): a card with the delivery's header information, built by `backOrderInformation`.
 * - Right (8 of 12 columns): a paginated table of the back-ordered lines,
 *   using the columns in `backOrderItemsColumn` and rows rendered by `BackOrderDetailsList`.
 *
 * Takes no props.
 * Pagination state (current page and rows per page) comes from the `usePagination` hook.
 *
 * @returns {JSX.Element} The back order detail page.
 */
function BackOrderDetails() {
  const { id } = useParams();
  const { currentPage, pageSize, handlePageChange, handleRowsPerPageChange } = usePagination();
  const { data, isLoading } = useQueryUrl("/backorders/" + id);

  if (isLoading) return <Loading />;

  return (
    <React.Fragment>
      {/* Title */}
      <PageTitle title={`Back Order Details`} />
      {/* Link */}
      <div style={{ display: "flex" }}>
        <DashboardLink />
        <NavigationLink name="Back Orders" url="/backorders" />
        <CurrentPage currentPage="Back Order Details" />
      </div>
      {/* Card layout */}
      <Grid container spacing={3}>
        {/* General order info */}
        <Grid item xs={12} md={4}>
          <DetailsCard
            title="Back Order Information"
            data={backOrderInformation(data)}
          />
        </Grid>
        {/* Order items */}
        <Grid item xs={12} md={8}>
          <PagedTable
            title="Back Order Items"
            columns={backOrderItemsColumn}
            length={data.backOrderItems.length}
            currentPage={currentPage}
            pageSize={pageSize}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          >
            <BackOrderDetailsList
              items={data.backOrderItems}
              currentPage={currentPage}
              pageSize={pageSize}
            />
          </PagedTable>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default BackOrderDetails;